import { initializeApp, type FirebaseApp } from "firebase/app";
import { getAuth, type Auth } from "firebase/auth";
import {
  clearIndexedDbPersistence, initializeFirestore, memoryLocalCache, memoryLruGarbageCollector, persistentLocalCache,
  persistentSingleTabManager, terminate,
  type Firestore, type FirestoreSettings,
} from "firebase/firestore";
import type { Laufzeitkonfiguration } from "./konfiguration";

/**
 * Firebase Web SDK (ADR-0027): eine App `[DEFAULT]`, Auth mit dem Mandanten des Stacks (identitaeten.md §1; die
 * Sitzung liegt in `firebaseLocalStorageDb`), Firestore der benannten Datenbank. Den persistenten Cache gibt es nur
 * am Jagdtag (ADR-0020); sonst liest die App nur mit Netz. Beginnt oder endet der Jagdtag, während die App läuft,
 * startet `firestoreUmstellen` Firestore neu; die Lesemodell-Hooks hören auf `firestoreBeobachten` und melden ihre
 * Listener an der neuen Instanz wieder an.
 */
export interface Dienste {
  app: FirebaseApp;
  auth: Auth;
  db: Firestore;
  konfig: Laufzeitkonfiguration;
}

let dienste: Dienste | null = null;
let persistent = false;
let generation = 0;
const hoerer = new Set<() => void>();
let umstellung: Promise<void> = Promise.resolve();

/**
 * Einstellungen jeder Firestore-Instanz der App.
 *
 * Transport (Bericht N12): Ohne Vorgabe streamt WebChannel die Antworten der Listener über eine einzige, offen bleibende
 * Antwort und prüft nur, ob deren erste Bytes rechtzeitig ankommen („buffering proxy detection“). Safari 16.4 liefert
 * diese ersten Bytes, danach aber nichts mehr: Die Listener warten dann ohne Fehler für immer (leere Startansicht,
 * „Keine Meldungen“, leerer Kanal). Dasselbe tun puffernde Proxys im Mobilfunk. Mit Long-Polling schließt der Server
 * jede Antwort ab, sobald Daten da sind; abgeschlossene Antworten kommen in jeder Engine und durch jeden Proxy an.
 */
export function firestoreEinstellungen(mitCache: boolean): FirestoreSettings {
  // Außerhalb des Jagdtags nichts auf dem Gerät, aber das schon Gelesene bleibt im Speicher der laufenden App, auch wenn
  // eine Ansicht ihren Listener abmeldet (LRU statt sofortigem Verwerfen): z. B. „Stand vor Ort“ ohne Netz (revier/05).
  const localCache = mitCache ? persistentLocalCache({ tabManager: persistentSingleTabManager({}) })
    : memoryLocalCache({ garbageCollector: memoryLruGarbageCollector() });
  return { localCache, experimentalForceLongPolling: true };
}

function firestoreAnlegen(app: FirebaseApp, konfig: Laufzeitkonfiguration, mitCache: boolean): Firestore {
  return initializeFirestore(app, firestoreEinstellungen(mitCache), konfig.firebase.firestoreDatabaseId);
}

export function firebaseStarten(konfig: Laufzeitkonfiguration, mitCache: boolean): Dienste {
  if (dienste) return dienste;
  const app = initializeApp({
    apiKey: konfig.firebase.apiKey, projectId: konfig.firebase.projectId, appId: konfig.firebase.appId,
    messagingSenderId: konfig.firebase.messagingSenderId, authDomain: `${konfig.firebase.projectId}.firebaseapp.com`,
  });
  const auth = getAuth(app);
  auth.tenantId = konfig.tenantId;
  persistent = mitCache;
  dienste = { app, auth, db: firestoreAnlegen(app, konfig, mitCache), konfig };
  return dienste;
}

export function firebaseDienste(): Dienste {
  if (!dienste) throw new Error("Firebase ist noch nicht gestartet");
  return dienste;
}

/** Läuft Firestore gerade mit dem persistenten Cache des Jagdtags? */
export function firestorePersistent(): boolean {
  return persistent;
}

/** Zähler der Firestore-Instanz; ändert sich bei jedem Neustart (`useSyncExternalStore`). */
export function firestoreGeneration(): number {
  return generation;
}

export function firestoreBeobachten(f: () => void): () => void {
  hoerer.add(f);
  return () => hoerer.delete(f);
}

/**
 * Startet Firestore neu: mit persistentem Cache (Jagdtag) oder ohne. `loeschen` entfernt vorher den persistenten
 * Cache (Ende des Jagdtags, Zugang entzogen, Daten unvollständig). Umstellungen laufen nacheinander.
 */
export function firestoreUmstellen(mitCache: boolean, loeschen: boolean): Promise<void> {
  umstellung = umstellung.then(async () => {
    const d = firebaseDienste();
    try {
      await terminate(d.db);
    } catch { /* eine beschädigte Instanz (Speicher geleert) wird trotzdem ersetzt */ }
    if (loeschen) {
      try {
        await clearIndexedDbPersistence(d.db);
      } catch { /* bereits entfernt */ }
    }
    persistent = mitCache;
    d.db = firestoreAnlegen(d.app, d.konfig, mitCache);
    generation += 1;
    for (const h of hoerer) h();
  });
  return umstellung;
}
